import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, X, Smartphone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline "Get a price-drop SMS" card for the HomePage.
 * Only shown to logged-in guest users; CTA for others is to sign in.
 */
export const PriceDropSubscribe = () => {
  const { user } = useAuth();
  const [city, setCity] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showList, setShowList] = useState(false);

  const fetchAlerts = useCallback(async () => {
    if (!user || user.role !== 'guest') return;
    try {
      const { data } = await axios.get(`${API}/guest/price-alerts`, { withCredentials: true });
      setAlerts(data.alerts || []);
    } catch (err) { console.error('[PriceDropSubscribe] fetchAlerts failed:', err); }
  }, [user]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const submit = async (e) => {
    e.preventDefault();
    if (!city.trim() || !maxPrice) {
      toast.error('Enter city and max price');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/guest/price-alerts`,
        { city: city.trim(), max_price: parseInt(maxPrice) },
        { withCredentials: true },
      );
      toast.success(data.message || 'Alert created');
      setCity('');
      setMaxPrice('');
      fetchAlerts();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Could not create alert');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/guest/price-alerts/${id}`, { withCredentials: true });
      toast.success('Alert removed');
      fetchAlerts();
    } catch (e) {
      toast.error('Could not remove');
    }
  };

  const activeAlerts = alerts.filter((a) => a.active);

  if (!user) {
    return (
      <div className="bg-gradient-to-br from-[#FFE5EE] to-white border border-[#FF0059]/20 rounded-2xl p-6 flex items-center gap-4" data-testid="price-drop-card-anon">
        <div className="w-12 h-12 rounded-full bg-[#FF0059] flex items-center justify-center flex-shrink-0">
          <Bell className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-black text-base text-black">Get SMS alerts on price drops</h3>
          <p className="text-xs text-gray-600 mt-0.5">Sign in to subscribe to a city &amp; max budget — we&apos;ll SMS when rates dip below.</p>
        </div>
        <a href="/login" className="px-4 py-2 rounded-full bg-[#FF0059] hover:bg-[#CC0047] text-white text-sm font-bold transition-colors flex-shrink-0" data-testid="price-drop-signin">
          Sign in
        </a>
      </div>
    );
  }

  if (user.role !== 'guest') return null;

  return (
    <div className="bg-gradient-to-br from-[#FFE5EE] to-white border border-[#FF0059]/20 rounded-2xl p-6" data-testid="price-drop-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-[#FF0059] flex items-center justify-center flex-shrink-0">
          <Bell className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-black text-base text-black">Get SMS alerts on price drops</h3>
          <p className="text-xs text-gray-600 mt-0.5 flex items-center gap-1">
            <Smartphone className="h-3 w-3" /> We&apos;ll text you when properties in your city drop below your budget.
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="price-drop-form">
        <input
          type="text"
          placeholder="City (e.g. Amritsar)"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          className="px-3 py-2.5 rounded-full border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-medium bg-white"
          data-testid="price-drop-city-input"
          required
        />
        <input
          type="number"
          min="500"
          max="100000"
          step="100"
          placeholder="Max ₹ / night"
          value={maxPrice}
          onChange={(e) => setMaxPrice(e.target.value)}
          className="px-3 py-2.5 rounded-full border border-gray-200 focus:border-[#FF0059] outline-none text-sm font-medium bg-white"
          data-testid="price-drop-price-input"
          required
        />
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2.5 rounded-full bg-[#FF0059] hover:bg-[#CC0047] text-white text-sm font-bold transition-colors disabled:opacity-60"
          data-testid="price-drop-submit-btn"
        >
          {submitting ? 'Saving…' : 'Notify me on SMS'}
        </button>
      </form>
      {activeAlerts.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowList((v) => !v)}
            className="text-xs font-bold text-[#FF0059] hover:underline"
            data-testid="price-drop-toggle-list"
          >
            {showList ? 'Hide' : 'Show'} my {activeAlerts.length} alert{activeAlerts.length === 1 ? '' : 's'}
          </button>
          {showList && (
            <ul className="mt-2 space-y-1.5" data-testid="price-drop-list">
              {activeAlerts.map((a) => (
                <li key={a._id} className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs" data-testid={`price-drop-row-${a._id}`}>
                  <span className="font-bold text-black">{a.city}</span>
                  <span className="text-gray-500">≤ ₹{a.max_price}/night</span>
                  <button
                    onClick={() => remove(a._id)}
                    className="p-1 rounded-full hover:bg-red-50 text-red-500"
                    aria-label="Remove alert"
                    data-testid={`price-drop-remove-${a._id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
